import { issuerName } from '@domain/legal/IssuerInfo';
import { getIssuerInfo } from '@infra/legal/IssuerInfoService';
import type { ViewController } from '@presentation/shell/NexxusShell';
import {
  NexxusBillingClient,
  type ModuleOffer,
  type NexxusInvoice,
} from '@infra/billing/NexxusBillingClient';
import { ToastService } from '@presentation/components/Toast';
import { previewDocument } from '@presentation/print/PdfService';
import { esc } from '@presentation/shell/format';

const money = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'XAF',
  maximumFractionDigits: 0,
});

export const modulesController: ViewController = async (section, ctx) => {
  const client = new NexxusBillingClient();
  const list = section.querySelector<HTMLElement>('#module-store-list');
  const summary = section.querySelector<HTMLElement>('#module-store-summary');
  const buy = section.querySelector<HTMLButtonElement>('#module-store-buy');
  if (!list || !summary || !buy) return;

  const loadCatalog = async (): Promise<void> => {
    buy.disabled = true;
    list.innerHTML =
      '<div class="card"><p style="color:var(--gris)">Chargement du catalogue Nexxus…</p></div>';
    try {
      const catalog = await client.catalog();
      if (!catalog.items.length) {
        list.innerHTML =
          '<div class="card"><strong>Aucun module à afficher</strong><p style="margin-top:8px;color:var(--gris)">Le catalogue Nexxus est vide pour cette licence. Contactez le support si des modules devraient être proposés.</p></div>';
        summary.textContent = 'Aucun module disponible.';
        return;
      }
      renderModuleOffers(list, catalog.items);
      const refreshSummary = () => {
        const selected = selectedOffers(list, catalog.items);
        const total = selected.reduce((sum, offer) => sum + (offer.amount_xaf ?? 0), 0);
        summary.textContent = selected.length
          ? `${selected.length} module(s) — ${money.format(total)}`
          : 'Sélectionnez les modules à ajouter.';
        buy.disabled = selected.length === 0;
      };
      list.onchange = refreshSummary;
      refreshSummary();
      buy.onclick = () => void purchase(client, list, catalog.items, ctx, section);
    } catch (error) {
      const trial = Boolean(ctx.getLicenceRuntime()?.getState()?.claims?.trial);
      const copy = catalogErrorCopy(error, { trial });
      list.innerHTML = `<div class="card module-store-error">
        <strong>${esc(copy.title)}</strong>
        <p>${esc(copy.detail)}</p>
        ${copy.hint ? `<p>${esc(copy.hint)}</p>` : ''}
        <button type="button" class="btn btn-secondary btn-sm" data-retry-catalog>Réessayer</button>
      </div>`;
      summary.textContent = 'Catalogue indisponible.';
      buy.disabled = true;
      list.querySelector('[data-retry-catalog]')?.addEventListener('click', () => {
        void loadCatalog();
      });
    }
  };

  await loadCatalog();
};

export function catalogErrorCopy(
  error: unknown,
  opts?: { trial?: boolean },
): { title: string; detail: string; hint?: string } {
  const message = error instanceof Error ? error.message : 'Service indisponible';
  if (message === 'Activation inactive') {
    return {
      title: 'Catalogue temporairement indisponible',
      detail: 'Le service n’a pas pu charger les modules pour cette licence.',
      hint: 'Réessayez dans un instant.',
    };
  }
  if (message === 'Licence locale indisponible') {
    return {
      title: 'Licence locale introuvable',
      detail: 'Aucun certificat d’activation n’est enregistré sur ce poste.',
      hint: 'Activez Nexxus avec la clé commerciale fournie par votre conseiller.',
    };
  }
  if (message === 'Certificat invalide' || message === 'Certificat non lié à ce poste') {
    return {
      title: 'Licence à renouveler',
      detail: message,
      hint: 'Paramètres → Renouveler en ligne, ou réactivation avec la clé commerciale.',
    };
  }
  if (
    message ===
    'Achat de modules indisponible pendant l’essai gratuit. Convertissez la licence après paiement.'
  ) {
    return {
      title: 'Catalogue visible — achat après conversion',
      detail: message,
      hint: 'Convertissez votre essai en licence commerciale pour ajouter des modules.',
    };
  }
  if (/fetch|Failed|network|timeout|injoignable|indisponible/i.test(message)) {
    return {
      title: 'Connexion au service Nexxus impossible',
      detail: opts?.trial
        ? 'Achat de modules indisponible hors connexion. Connectez-vous à internet et convertissez votre essai pour accéder au catalogue.'
        : 'Achat de modules indisponible hors connexion. Connectez-vous à internet pour consulter le catalogue de votre licence.',
      hint: opts?.trial
        ? 'Mode local / essai : le catalogue n’est pas stocké sur ce poste.'
        : 'Mode local : le catalogue n’est pas stocké sur ce poste.',
    };
  }
  return {
    title: 'Catalogue indisponible',
    detail: message,
    hint: 'Réessayez. Si le problème continue, contactez le support Nexxus.',
  };
}

export const nexxusInvoicesController: ViewController = async (section, ctx, shell) => {
  const host = section.querySelector<HTMLElement>('#nexxus-invoices-list');
  if (!host) return;
  try {
    const invoices = await new NexxusBillingClient().listInvoices();
    host.innerHTML = invoices.length
      ? invoices.map(invoiceCard).join('')
      : '<div class="card">Aucune facture Nexxus.</div>';
    host.querySelectorAll<HTMLButtonElement>('[data-invoice-id]').forEach((button) => {
      button.onclick = () => {
        const invoice = invoices.find((entry) => entry.id === button.dataset.invoiceId);
        if (invoice) printInvoice(invoice);
      };
    });
  } catch (error) {
    const copy = invoiceErrorCopy(error);
    host.innerHTML = `<div class="card module-store-error">
      <strong>${esc(copy.title)}</strong>
      <p style="margin-top:8px;color:var(--gris)">${esc(copy.detail)}</p>
      ${copy.hint ? `<p style="margin-top:8px;color:var(--gris)">${esc(copy.hint)}</p>` : ''}
      <button type="button" class="btn btn-secondary btn-sm" data-retry-invoices style="margin-top:12px">Réessayer</button>
    </div>`;
    host.querySelector('[data-retry-invoices]')?.addEventListener('click', () => {
      void nexxusInvoicesController(section, ctx, shell);
    });
  }
};

export function invoiceErrorCopy(error: unknown): {
  title: string;
  detail: string;
  hint?: string;
} {
  const message = error instanceof Error ? error.message : 'Chargement impossible';
  if (/fetch|Failed|network|timeout|injoignable|indisponible|Load failed/i.test(message)) {
    return {
      title: 'Factures indisponibles hors connexion',
      detail:
        'Les factures Nexxus (abonnement et modules) sont hébergées en ligne. En mode local hors ligne, cet écran ne peut pas les afficher.',
      hint: 'Reconnectez Internet puis rouvrez Factures Nexxus. Le reste de l’ERP (Paie, RH, Compta) continue de fonctionner hors ligne.',
    };
  }
  if (/inactive|expir/i.test(message)) {
    return {
      title: 'Licence serveur inactive ou expirée',
      detail:
        'Le serveur Nexxus refuse la lecture des factures pour cette licence (statut inactif ou date d’expiration dépassée côté abonnement).',
      hint: 'L’ERP local peut continuer à fonctionner. Pour les factures et modules : renouvelez la licence dans Paramètres, ou contactez le support Nexxus avec votre clé commerciale.',
    };
  }
  return {
    title: 'Impossible de charger les factures',
    detail: message,
    hint: 'Réessayez. Si le problème continue, contactez le support Nexxus.',
  };
}

export function renderModuleOffers(host: HTMLElement, items: ModuleOffer[]): void {
  host.innerHTML = items
    .map((offer) => {
      const state = offer.included
        ? { label: 'Inclus', cls: 'badge-success' }
        : offer.purchasable
          ? { label: money.format(offer.amount_xaf ?? 0), cls: 'badge-info' }
          : offer.amount_xaf === null
            ? { label: 'Indisponible', cls: 'badge-warning' }
            : { label: 'Vente désactivée', cls: 'badge-warning' };
      const availabilityHint =
        !offer.included && !offer.purchasable && offer.amount_xaf !== null
          ? '<span class="module-offer-hint">Ce module possède un tarif, mais sa vente doit être activée par Nexxus.</span>'
          : '';
      return `<label class="card module-offer-card ${offer.purchasable ? 'is-purchasable' : ''} ${offer.included ? 'is-included' : ''}"><input type="checkbox" data-feature-code="${esc(offer.feature_code)}" ${offer.purchasable ? '' : 'disabled'}><span class="module-offer-body"><strong>${esc(offer.label)}</strong><span class="module-offer-desc">${esc(offer.description ?? '')}</span>${availabilityHint}</span><span class="badge ${state.cls}">${state.label}</span></label>`;
    })
    .join('');
}

function selectedOffers(host: HTMLElement, offers: ModuleOffer[]): ModuleOffer[] {
  const codes = new Set(
    Array.from(host.querySelectorAll<HTMLInputElement>('input:checked')).map(
      (input) => input.dataset.featureCode,
    ),
  );
  return offers.filter((offer) => codes.has(offer.feature_code));
}

async function purchase(
  client: NexxusBillingClient,
  host: HTMLElement,
  offers: ModuleOffer[],
  ctx: Parameters<ViewController>[1],
  section: HTMLElement,
): Promise<void> {
  const selected = selectedOffers(host, offers);
  if (!selected.length) return;
  const total = selected.reduce((sum, offer) => sum + (offer.amount_xaf ?? 0), 0);
  if (!(await orderDialog(section, selected, total))) return;
  try {
    // SEC-04 : le poste ne confirme jamais un paiement. La commande reste « en attente » jusqu'à la
    // validation du virement par Nexxus ; les modules arrivent ensuite avec la synchronisation des droits.
    const order = await client.createOrder(selected.map((offer) => offer.feature_code));
    ToastService.success(
      `Commande ${order.id.slice(0, 8).toUpperCase()} enregistrée. Réglez par virement bancaire : les modules seront activés après validation du paiement par Nexxus.`,
    );
    await modulesController(section, ctx, {} as never);
  } catch (error) {
    ToastService.error(error instanceof Error ? error.message : 'Commande impossible');
  }
}

function orderDialog(section: HTMLElement, offers: ModuleOffer[], total: number): Promise<boolean> {
  return new Promise((resolve) => {
    const modal = document.createElement('nexxus-modal') as HTMLElement & {
      open: () => void;
      close: () => void;
    };
    modal.setAttribute('title', 'Récapitulatif de la commande');
    modal.setAttribute('size', 'md');
    modal.innerHTML = `<div class="modal-body"><div class="card" style="margin-bottom:16px">${offers.map((offer) => `<div style="display:flex;justify-content:space-between;gap:12px;padding:9px 0;border-bottom:1px solid var(--border)"><span>${esc(offer.label)}</span><strong>${money.format(offer.amount_xaf ?? 0)}</strong></div>`).join('')}<div style="display:flex;justify-content:space-between;gap:12px;padding-top:16px;font-size:18px"><strong>Total</strong><strong>${money.format(total)}</strong></div></div><p class="hint">Règlement par virement bancaire. Nexxus vérifie le paiement puis active les modules et émet la facture ; aucun module n’est activé avant cette validation.</p><div class="module-store-bar" style="position:static;margin-top:20px;box-shadow:none;padding:0"><button type="button" class="btn btn-ghost" data-cancel>Annuler</button><button type="button" class="btn btn-primary" data-confirm>Passer la commande</button></div></div>`;
    section.appendChild(modal);
    const finish = (value: boolean) => {
      modal.close();
      modal.remove();
      resolve(value);
    };
    modal.querySelector('[data-cancel]')?.addEventListener('click', () => finish(false));
    modal.querySelector('[data-confirm]')?.addEventListener('click', () => finish(true));
    modal.open();
  });
}

function invoiceCard(invoice: NexxusInvoice): string {
  return `<article class="card"><div class="card-title"><span>${esc(invoice.invoiceNumber)}</span><span class="badge badge-success">Payée</span></div><p>${new Date(invoice.issuedAt).toLocaleDateString('fr-FR')} — <strong>${money.format(invoice.totalXaf)}</strong></p><p style="margin-top:8px;color:var(--gris)">${invoice.items.map((item) => esc(item.label)).join(', ')}</p><button class="btn btn-secondary btn-sm" data-invoice-id="${esc(invoice.id)}" style="margin-top:14px">Afficher / enregistrer en PDF</button></article>`;
}

function printInvoice(invoice: NexxusInvoice): void {
  const customer = invoice.customerSnapshot;
  // Identité de l'émetteur : informations saisies dans la console (copie signée en cache) — jamais en dur.
  const issuer = getIssuerInfo();
  const issuerMeta =
    [
      issuer.head_office,
      issuer.nif ? `NIF ${issuer.nif}` : '',
      issuer.rccm ? `RCCM ${issuer.rccm}` : '',
    ]
      .filter((x) => x !== '')
      .join(' · ') || 'Nexxus ERP';
  const lignes = invoice.items
    .map(
      (item) =>
        `<tr><td>${esc(item.label)}</td><td class="num">${money.format(item.amount_xaf)}</td></tr>`,
    )
    .join('');
  const html = `<div class="bulletin" id="nexxus-invoice-print">
    <div class="bulletin-header">
      <div><div class="company">${esc(issuerName(issuer))}</div><div class="meta">${esc(issuerMeta)}</div></div>
      <div style="text-align:right;"><div class="company">FACTURE</div><div class="meta">${esc(invoice.invoiceNumber)} · ${new Date(invoice.issuedAt).toLocaleDateString('fr-FR')}</div></div>
    </div>
    <div class="bulletin-meta-section">
      <div class="group"><h4>Client</h4>
        <div class="row"><span class="lbl">Raison sociale</span><span class="val">${esc(String(customer.legal_name ?? ''))}</span></div>
        <div class="row"><span class="lbl">Email</span><span class="val">${esc(String(customer.email ?? ''))}</span></div>
      </div>
    </div>
    <table class="bulletin-table">
      <thead><tr><th>Module</th><th class="num">Montant</th></tr></thead>
      <tbody>${lignes}</tbody>
    </table>
    <div class="bulletin-totals">Total TTC <strong>${money.format(invoice.totalXaf)}</strong></div>
  </div>`;
  previewDocument(html, `${invoice.invoiceNumber}.pdf`, `Facture ${invoice.invoiceNumber}`);
}
